import { getMeanFeesForOffer } from 'lib/planAndOffer';
import {
  Capabilities,
  Feature,
  Offer,
  OfferType,
  QuoteInformation,
  useCapabilities,
  useCapabilitiesFirstOffer,
  useOffer,
  useOffers,
} from '@app/api';
import { useMemo } from 'react';
import { useFormikContext } from 'formik';

export const useOfferInfos = () => {
  const { values } = useFormikContext<Partial<QuoteInformation>>();
  const capabilities = useCapabilities();

  return useOfferInfosFor(values, capabilities);
};

export const useOfferInfosFor = (
  values: Partial<QuoteInformation>,
  capabilities?: Capabilities,
) => {
  const { data: offers } = useOffers();

  return useMemo(
    () => getOfferInfos(values, offers, capabilities),
    [values, offers, capabilities],
  );
};

export const getOfferInfos = (
  values: Partial<QuoteInformation>,
  offers?: Offer[],
  capabilities?: Capabilities,
) => {
  const availableOffers = filterOffers(values, offers);

  return {
    availableOffers,
    targetingOffers: availableOffers.filter(
      (offer) =>
        offer.type === OfferType.TARGETING &&
        offer.products.find((p) => !p.forRecruitmentByClient),
    ),
    ownRecruitmentOffer: offers?.find(
      (offer) =>
        offer.type === OfferType.TARGETING &&
        !offer.products.find((p) => !p.forRecruitmentByClient),
    ),
    optionOffers: removeAlreadyUnlocked(
      availableOffers.filter((offer) => offer.type === OfferType.OPTION),
      capabilities,
    ),
    supportOffers: availableOffers.filter(
      ({ type }) => type === OfferType.SUPPORT,
    ),
  };
};

export const useInvoiceFeeOffer = () => {
  const sendInvoiceCapabilityOffer = useCapabilitiesFirstOffer(
    Feature.SEND_INVOICE,
  );
  const { data: invoiceFeeOffer } = useOffer(sendInvoiceCapabilityOffer?.id);

  return invoiceFeeOffer;
};

export const filterOffers = (
  values: Partial<QuoteInformation>,
  offers?: Offer[],
): Offer[] => {
  if (!offers) {
    return [];
  }
  const { studySubject, studyType, format } = values;
  if (!studySubject || !studyType) {
    return [];
  }

  const res = offers
    .filter(({ requirement }) => {
      return (
        requirement.studyTypes.includes(studyType) &&
        requirement.studySubjects.includes(studySubject) &&
        (!requirement.formats ||
          !format ||
          requirement.formats.includes(format)) &&
        (!requirement.priceThierId ||
          requirement.priceThierId === values.priceThierId)
      );
    })
    .sort((offer1, offer2) => {
      return getMeanFeesForOffer(offer1) - getMeanFeesForOffer(offer2);
    });
  return res;
};

export const requirePriceTier = (
  values: Partial<QuoteInformation>,
  offers?: Offer[],
): boolean => {
  if (!offers) {
    return false;
  }
  const { studySubject, studyType, format } = values;
  if (!studySubject || !studyType) {
    return false;
  }

  return offers
    .filter(({ requirement }) => {
      return (
        requirement.studyTypes.includes(studyType) &&
        requirement.studySubjects.includes(studySubject) &&
        (!requirement.formats ||
          !format ||
          requirement.formats.includes(format))
      );
    })
    .some((offer) => !!offer.requirement.priceThierId);
};

export const removeAlreadyUnlocked = (
  offers?: Offer[],
  capabilities?: Capabilities,
) => {
  if (!offers) {
    return [];
  }
  if (!capabilities) {
    return offers;
  }

  return offers.filter((offer) => {
    const remainingProducts = offer.products.filter(
      (p) => !p.product?.feature || !capabilities[p.product?.feature],
    );
    return remainingProducts.length > 0;
  });
};
